import request from 'lib/request';
import { mutationAtomWithLoadable } from 'lib/atoms';
import { createFileAtom } from 'lib/atoms/file-atom';
import type { AriesTemplateUploadResponse } from './types';

// ---------------------------------------------------------------------------
// Aries MDB Export — "Load a new Template" upload atoms (INS-10555)
//
// The modal-internal template-upload sub-flow, shared by both eval pages. The
// export defaults / kickoff / restore atoms are calc-scoped and stay with each
// page (they thread the page's own calculation id); only the template-upload
// atoms live here, since they back the shared modal's drop zone.
// ---------------------------------------------------------------------------

// Single-file Active Storage direct-upload atom backing the "Load a new
// Template" .mdb drop zone. One module-level instance (mirroring
// add-document-modal's `fileAtom`) so the hook reads the same upload lifecycle
// it writes; the modal resets it on open to clear a prior selection.
export const ariesTemplateFileAtom = createFileAtom();

// POST /aries_templates — register an already direct-uploaded .mdb blob as a new
// AriesTemplate, optionally making it the global default. `attachment` is the
// signed id from the direct upload (not raw bytes). Returns the created template
// (its `id` feeds the export kickoff's `templateId`). The Rails 422 for a
// non-.mdb / structurally-invalid template arrives as `{ errors: { attachment:
// [...] } }`; surface that message so the drop zone can show it inline.
export const [ariesTemplateUploadBaseAtom] = mutationAtomWithLoadable<
  AriesTemplateUploadResponse,
  [string, boolean]
>(async (_get, _set, signedId, makeDefault) => {
  const response = await request.post<
    AriesTemplateUploadResponse,
    { errors?: Record<string, string[]>; error?: string }
  >(
    '/aries_templates',
    { ariesTemplate: { attachment: signedId, makeDefault } },
    new AbortController()
  );
  if (response.type === 'success') {
    return response.data;
  }
  const failureMessage =
    response.type === 'error'
      ? (response.data?.errors?.attachment?.[0] ??
        response.data?.error ??
        'Failed to upload template')
      : (response.message ?? 'Failed to upload template');
  throw new Error(failureMessage);
});
